import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from 'react-i18next';

// Highcharts: all 4 Imports are needed - https://www.highcharts.com/blog/tutorials/highcharts-wrapper-for-react-101/
import Highcharts from "highcharts/highcharts.js";
import highchartsMore from "highcharts/highcharts-more.js"
import solidGauge from "highcharts/modules/solid-gauge.js";
import HighchartsReact from "highcharts-react-official";

import ClearFilterBtn from "../../../application/ClearFilterBtn";
import useDataSource from "../../../../hooks/useDataSource";
import useMainCanvasSize from "../../../../hooks/useMainCanvasSize";
import useWindowSize from "../../../../hooks/useWindowSize";

import {
    formatNumber
} from "../../../../utilities/FormatUtilities";
import SettingsForm from "../settings/SettingsForm";
import {appSetModalContent} from "../../../../redux/app/appActions";
import {findComponent} from "../../../../redux/page/pageActions";

import { styled, Card, CardHeader, CardContent, LinearProgress } from '@mui/material';

const PREFIX = 'ActivityGauge';

const classes = {
    root: `${PREFIX}-root`,
    linearProgress: `${PREFIX}-linearProgress`,
    cardHeader: `${PREFIX}-cardHeader`
};

const Root = styled('div')((
    {
        theme
    }
) => ({
    [`&.${classes.root}`]: {
        height: 'calc(100% - 1px)'
    },

    [`& .${classes.linearProgress}`]: {
        height: '1px'
    },

    [`& .${classes.cardHeader}`]: {
        padding: '8px 8px 0px 8px',
        alignItems: 'start',
        textAlign: 'left',
        textTransform: 'none'
    }
}));

// export default function ActivityGauge({id, title, subtitle, card, highchartOptions, dataSourceName, showFetching, fields}) {
export default function ActivityGauge({id, configuration, xId, onLoading, onLoaded}) {

	const dispatch = useDispatch();
    const { t } = useTranslation();

    // App State
    const persist = useSelector(state => state.persist);
	const pageID = useSelector(state => state.app.pageID);
	const editXId = useSelector(state => state.page.componentEdit.xId);
	const componentUpdated = useSelector(state => state.page.componentUpdated);
	const componentWrapperEdit = useSelector(state => state.page.componentWrapperEdit);
	const pages = useSelector(state => state.page.pages);
	const pageConfigs = useSelector(state => state.config.data.pageConfigs);
	const activeRole = useSelector(state => state.persist.activeRoleName);

    // Component State
	const [compConfig, setCompConfig] = useState(configuration);
	const [title, setTitle] = useState(compConfig.name?.title ? compConfig.name.title : '');
	const [subtitle, setSubTitle] = useState(compConfig.name?.subtitle ? compConfig.name.subtitle : '');
    const [chart, setChart] = useState(null);
    const [chartData, setChartData] = useState(null);
    const [noDataMessage, setNoDataMessage] = useState("No Data");
    const [showProgress, setShowProgress] = useState(isFunction(onLoading) ? false : true);
	const [cardHeaderStyle, setCardHeaderStyle] = useState({});
	const [cardContentStyle, setCardContentStyle] = useState({});
	const [resizedComponent, setResizedComponent] = useState(false);

	const highchartOptions = compConfig?.highchartOptions ? compConfig?.highchartOptions : {};
	const dataSourceName = compConfig.dataSource?.name ? compConfig.dataSource?.name : '';
	const fields = compConfig.dataSource?.fields ? compConfig.dataSource?.fields : [];
	const colors = compConfig?.element?.colors;
	const styles = compConfig?.element?.styles;

	const chartRef = useRef(null);
	const chartContainer = useRef(null);
	const dataSource = useDataSource(dataSourceName);
	const size = useMainCanvasSize();
	const windowSize = useWindowSize();


    const radius = [
        {
            outerRadius: '112%',
            innerRadius: '88%',
        },
        {
            outerRadius: '87%',
            innerRadius: '63%',
        },
        {
            outerRadius: '62%',
            innerRadius: '38%',
        }
    ]

    // Mount
    useEffect(()=> {

        return function cleanUp(){
            if (chart) {
                chart.destroy();
            }
        }
    }, []);

    // Loading
    useEffect(() => {
        if (dataSource.fetching === true) {
            if (isFunction(onLoading)) {
                onLoading();
            }
        }
    }, [dataSource.fetching]);

    // Loaded
    useEffect(() => {
        if (dataSource.fetching === false && dataSource.fetched === true) {
            if (isFunction(onLoaded)) {
                onLoaded();
            }
        }
    }, [dataSource.fetched]);

	// Did Component Wrapper Trigger Redux State Change?
	useEffect(() => {
		if (componentWrapperEdit.parentId && componentWrapperEdit.childId && xId) {
			if ((componentWrapperEdit.parentId === xId) || (componentWrapperEdit.childId === xId)) {
				const page = pages.find(page => page.name === pageID);
				for (let i = 0; i < pageConfigs.length; i++ ) {
					const roleConfig = page.roles.find(r => r.name === activeRole)[pageConfigs[i]];
					if (roleConfig) {
						const findResult = findComponent(xId, componentWrapperEdit.childId === xId ? componentWrapperEdit.parentId : null, roleConfig.configuration.components);
						if (findResult) {
							setCompConfig(findResult.configuration);
							break;
						}
					}
				}
			}
		}
	},[componentWrapperEdit]);

	// Component Configuration (compConfig) changed, reset state
	useEffect(() => {
		setTitle(compConfig.name?.title ? compConfig.name.title : '');
		setSubTitle(compConfig.name?.subtitle ? compConfig.name.subtitle : '');
	},[compConfig]);

	useEffect(() => {
		resizeCard();
	},[title, subtitle]);

	// componentUpdated & Resized
	useEffect(() => {
		if (xId) {
			if (componentUpdated.id === xId) {
				setCompConfig(componentUpdated.config);
				setResizedComponent(true);
			}
		}
	}, [componentUpdated]);

	// xEdit
	useEffect(() => {
		if (editXId) {
			if ((editXId === xId) && (pageID)) {

				const placeholders = {
					title: title,
					subtitle: subtitle
				}

				const settingsConfig = {
					title: 'Activity Gauge Configuration',
					parent: {
						minWidth: '400px',
						maxWidth: '400px',
						minHeight: '520px',
						maxHeight: '520px'
					},
					paper: null,
					button: null,
					content: <SettingsForm pageID={pageID} xId={xId} configuration={compConfig} schemaType={'ActivityGauge'} placeholders={placeholders}/>,
					tooltipContent: null
				}
				dispatch(appSetModalContent(settingsConfig));
			}
		}
	},[editXId, pageID]);

    useEffect(() => {

        // console.log('-----------------ACTIVITY GAUGE---------STATUS->');
        // console.log('fetching: {0}'.format(dataSource.fetching));
        // console.log('fetched: {0}'.format(dataSource.fetched));
        // console.log('error: {0}'.format(dataSource.error));
        // console.log('data:', dataSource.data);

        if (dataSource.error) {
            setNoDataMessage("Error");
            setChartData([]);
        }
        else if (dataSource.data && dataSource.fetched) {

            setNoDataMessage("No Data");

            // Setup base data as filtered results or a
            // baseline if included
            let data = dataSource.data;
            // data = data.concat(data);

            if (dataSource.includeBaselineResults && dataSource.baselines.length > 0) {
                // Find my baseline
                const baseline = dataSource.baselines.find(d => {
                    return d.id === id
                });

                // console.log('------------BASELINE------------->', baseline);
                if (baseline) {
                    let useBaseline = true;

                    // console.log('------------BASELINE------------->', baseline);
                    if (baseline) {
                        let useBaseline = true;

                        if (useBaseline) {
                            data = baseline.result;
                        }
                    }
                }
            }

            // Update Data/Chart
            setChartData(data);
        }
    }, [dataSource.data, dataSource.error]);

    useEffect(() => {

        updateChart();

    }, [chartData, persist.themeLight]);

    useEffect( () => {
        if (chart) {
            // setTimeout required to accurately obtain chart.renderTo.clientHeight and chart.renderTo.clientWidth to perform the reflow
            setTimeout(function() {
				chart.setSize(null, (chartContainer.current.offsetHeight * .80));	// 80% of the Root Div Width & Height
                chart.reflow();
				setResizedComponent(resizedComponent === true ? false : false);
            }, 300)
        }
	}, [size, windowSize, resizedComponent, cardHeaderStyle, cardContentStyle])

    function isFunction(functionToCheck) {
        return functionToCheck && {}.toString.call(functionToCheck) === '[object Function]';
    }

    // In the chart render event, add icons on top of the circular shapes
    // In the chart render event, add icons on top of the circular shapes
    function renderIcons(series) {

        //console.log(series);
        //console.log(Highcharts.chart.renderer);
        // highchartsMore(Highcharts);
        // const renderer = new Highcharts.Renderer(
        //     chartRef.current,
        //     400,
        //     300
        // );
        // console.log(renderer);
        // // Move icon
        // if (!series[0].icon) {
        //     series[0].icon = renderer.path(['M', -8, 0, 'L', 8, 0, 'M', 0, -8, 'L', 8, 0, 0, 8])
        //         .attr({
        //             stroke: '#303030',
        //             'stroke-linecap': 'round',
        //             'stroke-linejoin': 'round',
        //             'stroke-width': 2,
        //             zIndex: 10
        //         })
        //         .add(series[0].group);
        // }
        // console.log(series[0].icon);
        // if (series[0].icon) {
        //     series[0].icon.translate(
        //         renderer.width / 2 - 10,
        //         renderer.height / 2 - series[0].points[0].shapeArgs.innerR -
        //         (series[0].points[0].shapeArgs.r - series[0].points[0].shapeArgs.innerR) / 2
        //     );
        // }

        // Exercise icon
        // if (!series[1].icon) {
        //     series[1].icon = renderer.path(['M', -8, 0, 'L', 8, 0, 'M', 0, -8, 'L', 8, 0, 0, 8,'M', 8, -8, 'L', 16, 0, 8, 8])
        //         .attr({
        //             stroke: '#ffffff',
        //             'stroke-linecap': 'round',
        //             'stroke-linejoin': 'round',
        //             'stroke-width': 2,
        //             zIndex: 10
        //         })
        //         .add(series[1].group);
        // }
        // series[1].icon.translate(
        //     renderer.chartWidth / 2 - 10,
        //     renderer.plotHeight / 2 - series[1].points[0].shapeArgs.innerR -
        //     (series[1].points[0].shapeArgs.r - series[1].points[0].shapeArgs.innerR) / 2
        // );
        //
        // // Stand icon
        // if (!series[2].icon) {
        //     series[2].icon = renderer.path(['M', 0, 8, 'L', 0, -8, 'M', -8, 0, 'L', 0, -8, 8, 0])
        //         .attr({
        //             stroke: '#303030',
        //             'stroke-linecap': 'round',
        //             'stroke-linejoin': 'round',
        //             'stroke-width': 2,
        //             zIndex: 10
        //         })
        //         .add(series[2].group);
        // }
        // series[2].icon.translate(
        //     renderer.chartWidth / 2 - 10,
        //     renderer.plotHeight / 2 - series[2].points[0].shapeArgs.innerR -
        //     (series[2].points[0].shapeArgs.r - series[2].points[0].shapeArgs.innerR) / 2
        // );
    }

    const updateChart = () => {

        const data = chartData;

        if (chart === null && (data === null || data.length === 0))
            return;

        if (chart && (data === null || data.length === 0)) {
            // No Data
            chart.update({
                series: [{
                    name: '{0}'.format(fields[0].name),
                    data: []
                }]
            }, true, true);

            // NOTE: Indicates bigger issue in that update is still running after
            // the component is removed from DOM
            if (typeof chart.showNoData === 'function') {
                chart.showNoData();
            }
            return;
        }

        // Setup series
        const series = [];
        let v = 0
        for (let d = 0; d < fields.length; d++) {
            const item = data;
            let value = item[0][fields[d].name];
            if (value && (Math.sign(value) >= 0)) {
                series.push({
                    name: fields[d].label,
                    data: [{
                        color: colors ? colors[v] : Highcharts.getOptions().colors[v], //getDataColor(d, value, config.data.colorMap),
                        radius: radius[v].outerRadius,
                        innerRadius: radius[v].innerRadius,
                        y: formatNumber(value, {
                                abbreviate: false,
                                addSeparators: false,
                                currency: false,
                                decimals: 0,
                                default: '--',
                                fixDecimals: false,
                                round: true
                            }
                        )
                    }]
                });
                v += 1;
            }
        }

        // Pane Background
        const paneBackground = [];
        let y = 0;
        for (let d = 0; d < series.length; d++) {
            paneBackground.push({
                outerRadius: radius[y].outerRadius,
                innerRadius: radius[y].innerRadius,
                backgroundColor: Highcharts.color( colors ? colors[y] : Highcharts.getOptions().colors[y])
                    .setOpacity(0.3)
                    .get(),
                borderWidth: 0
            });
            y += 1;
        }

        if (!chart) {

            const chartOptions = {
                //...highchartOptions,
                chart: {
                    type: 'solidgauge',
                    height: highchartOptions.height ? highchartOptions.height : '100%',
                    backgroundColor: 'rgba(0,0,0,0)'
                },
                credits: {
                    enabled: false
                },
                // Card Attribution Used
                title: {
                    text: '',
                    margin: 0
                },
                subtitle: {
                    text: '',
                    margin: 0
                },
                lang: {
                    noData: noDataMessage
                },
                noData: {
                    style: {
                        fontWeight: 'bold',
                        fontSize: '15px',
                        color: '#303030'
                    }
                },
                tooltip: {
                    ...(highchartOptions.tooltip ? highchartOptions.tooltip : {}),
                    borderWidth: 0,
                    backgroundColor: 'none',
                    shadow: false,
                    style: {
                        fontSize: '12px'
                    },
                    useHTML: true,
                    formatter: function() {
                        return '<table><tr><td style="text-align: center; color:' + (persist.themeLight ? "#727272" : "#ffffff") + '; font-size:' + highchartOptions.tooltipFontSize.label + '">' + this.point.series.name + '</td></tr><tr><td style="text-align: center;"><span style="font-size:' + highchartOptions.tooltipFontSize.value + ';color:' + (persist.themeLight ? "#727272" : "#ffffff") + '; font-weight:bold;">' + this.y + (highchartOptions.tooltip.valueSuffix ? highchartOptions.tooltip.valueSuffix : '') +'</span></td></tr></table>'
                    },
                    // pointFormat: '<div style="width:100%;text-align:center;"><span style="font-size:1.2em;font-weight:bold;">{series.name}</span><br/><span style="font-size:3em;color:{point.color};font-weight:bold;">{point.y}</span>',
                    // pointFormat: '<div style="text-align: center;">{series.name}<br><span style="font-size:2em; color: {point.color}; font-weight: bold">{point.y}</span></div>',
                    positioner: function (labelWidth, labelHeight, point) {
                        return {
                            x: highchartOptions.tooltipPositionX ? point.plotX + highchartOptions.tooltipPositionX : point.plotX +  0, //(this.chart.chartWidth - labelWidth) / highchartOptions.tooltipPositionX,
                            y: highchartOptions.tooltipPositionY ? point.plotY + highchartOptions.tooltipPositionY : point.plotY + 0 //(this.chart.plotHeight / 4) + highchartOptions.tooltipPositionY
                        };
                    }
                },
                pane: {
                    ...(highchartOptions.pane ? highchartOptions.pane : {}),
                    background: paneBackground
                },
                yAxis: {
                    min: 0,
                    max: 100,
                    lineWidth: 0,
                    tickPositions: []
                },
                plotOptions: {
                    solidgauge: {
                        linecap: 'round',
                        stickyTracking: false,
                        rounded: true
                    },
                    series: {
                        dataLabels: [{
                            ...(highchartOptions.plotOptions.solidgauge.dataLabels ? highchartOptions.plotOptions.solidgauge.dataLabels : {}),
                            formatter: function() {
                                // return '<div style="width:100%;text-align:center;"><span style="font-size:1.2em;font-weight:bold;">' + this.point.series.name + '</span><br/><span style="font-size:3em;color:' + Highcharts.getOptions().colors[0] + ';font-weight:bold;">' + this.y + '</span>';
                                // return '<table><tr><td style="text-align: center;">' + this.point.series.name + '</td></tr><tr><td style="text-align: center;"><span style="font-size:2em;color:' + this.point.series.color + ';font-weight:bold;">hot dog day%</span></td></tr></table>';
                                return '<table><tr><td style="text-align: center;">' + subtitle + '</td></tr></table>';
                            }
                        }],
                        // point: {
                        //     events: {
                        //         mouseOver: function(){
                        //             if (highchartOptions.plotOptions.solidgauge.dataLabels.enabled) {
                        //                 // let e = !this.dataLabels || !this.dataLabels.enabled ? false : true;
                        //                 this.update({
                        //                     dataLabels:{
                        //                         enabled: false
                        //                     }
                        //                 });
                        //             }
                        //         },
                        //         mouseOut: function(){
                        //             if (highchartOptions.plotOptions.solidgauge.dataLabels.enabled) {
                        //                 // let e = !this.dataLabels || !this.dataLabels.enabled ? true : false;
                        //                 this.update({
                        //                     dataLabels: {
                        //                         enabled: true
                        //                     }
                        //                 });
                        //             }
                        //         }
                        //     }
                        // }
                    }
                },
                series: series
            };

            // console.log('ActivityGauge: Create', chartOptions);

            highchartsMore(Highcharts);
            solidGauge(Highcharts);
            setChart(Highcharts.chart(chartRef.current, chartOptions));
        } else {

            // console.log('ActivityGauge: Update');

            chart.update({
                // NOTE: We need to update our handlers otherwise they will point to
                // a previously defined function with a context that does not match
                // the current state - to reproduce comment out plot options
                // filter once chart, clear, then filter the 2nd chart and all
                // previously applied filters will be displayed
                tooltip: {
                    ...(highchartOptions.tooltip ? highchartOptions.tooltip : {}),
                    borderWidth: 0,
                    backgroundColor: 'none',
                    shadow: false,
                    style: {
                        fontSize: '12px'
                    },
                    useHTML: true,
                    formatter: function() {
                        return '<table><tr><td style="text-align: center; color:' + (persist.themeLight ? "#727272" : "#ffffff") + '; font-size:' + highchartOptions.tooltipFontSize.label + '">' + this.point.series.name + '</td></tr><tr><td style="text-align: center;"><span style="font-size:' + highchartOptions.tooltipFontSize.value + ';color:' + (persist.themeLight ? "#727272" : "#ffffff") + '; font-weight:bold;">' + this.y + (highchartOptions.tooltip.valueSuffix ? highchartOptions.tooltip.valueSuffix : '') + '</span></td></tr></table>'
                    },
                    // pointFormat: '<div style="width:100%;text-align:center;"><span style="font-size:1.2em;font-weight:bold;">{series.name}</span><br/><span style="font-size:3em;color:{point.color};font-weight:bold;">{point.y}</span>',
                    // pointFormat: '<div style="text-align: center;">{series.name}<br><span style="font-size:2em; color: {point.color}; font-weight: bold">{point.y}</span></div>',
                    positioner: function (labelWidth, labelHeight, point) {
                        return {
                            x: highchartOptions.tooltipPositionX ? point.plotX + highchartOptions.tooltipPositionX : point.plotX +  0, //(this.chart.chartWidth - labelWidth) / highchartOptions.tooltipPositionX,
                            y: highchartOptions.tooltipPositionY ? point.plotY + highchartOptions.tooltipPositionY : point.plotY + 0 //(this.chart.plotHeight / 4) + highchartOptions.tooltipPositionY
                        };
                    }
                },
                pane: {
                    ...(highchartOptions.pane ? highchartOptions.pane : {}),
                    background: paneBackground
                },
                plotOptions: {
                    solidgauge: {
                        linecap: 'round',
                        stickyTracking: false,
                        rounded: true
                    },
                    series: {
                        dataLabels: [{
                            ...(highchartOptions.plotOptions.solidgauge.dataLabels ? highchartOptions.plotOptions.solidgauge.dataLabels : {}),
                            formatter: function() {
                                // return '<div style="width:100%;text-align:center;"><span style="font-size:1.2em;font-weight:bold;">' + this.point.series.name + '</span><br/><span style="font-size:3em;color:' + Highcharts.getOptions().colors[0] + ';font-weight:bold;">' + this.y + '</span>';
                                // return '<table><tr><td style="text-align: center;">' + this.point.series.name + '</td></tr><tr><td style="text-align: center;"><span style="font-size:2em;color:' + this.point.series.color + ';font-weight:bold;">hot dog day%</span></td></tr></table>';
                                return '<table><tr><td style="text-align: center;">' + subtitle + '</td></tr></table>';
                            }
                        }],
                        // point: {
                        //     events: {
                        //         mouseOver: function(){
                        //             if (highchartOptions.plotOptions.solidgauge.dataLabels.enabled) {
                        //                 // let e = !this.dataLabels || !this.dataLabels.enabled ? false : true;
                        //                 this.update({
                        //                     dataLabels:{
                        //                         enabled: false
                        //                     }
                        //                 });
                        //             }
                        //         },
                        //         mouseOut: function(){
                        //             if (highchartOptions.plotOptions.solidgauge.dataLabels.enabled) {
                        //                 // let e = !this.dataLabels || !this.dataLabels.enabled ? true : false;
                        //                 this.update({
                        //                     dataLabels: {
                        //                         enabled: true
                        //                     }
                        //                 });
                        //             }
                        //         }
                        //     }
                        // }
                    }
                },
                series: series
            }, true, true);

        }
    };

	const resizeCard = () => {

		const newCardHeaderStyle = Object.assign({}, {...styles?.cardHeader});
		const newCardContentStyle = Object.assign({}, {
			...styles?.cardContent
			, height: 'calc(100% - 49px)'
			, overflow: 'hidden'
			, padding: '0px'
			, display: 'flex'
			, flexDirection: 'column'
			, justifyContent: 'center'
		});

		if (title && subtitle) {
			// Default Style
		} else if (title) {
			// Title Only
			newCardHeaderStyle.height = '29px';
			newCardContentStyle.height = 'calc(100% - 29px)';
		} else if (subtitle) {
			// Subtitle Only
			newCardHeaderStyle.height = '29px';
			newCardContentStyle.height = 'calc(100% - 29px)';
		} else {
			// No title
			newCardHeaderStyle.height = '0px';
			newCardHeaderStyle.padding = '0px';
			newCardContentStyle.height = '100%';
		}
		setCardHeaderStyle(newCardHeaderStyle);
		setCardContentStyle(newCardContentStyle);
	}

    return (
        <Root className={classes.root} ref={chartContainer}>
            {showProgress && dataSource.fetching &&
            <LinearProgress className={classes.linearProgress}/>
            }
            {showProgress && !dataSource.fetching &&
            <div className={classes.linearProgress}></div>
            }
            <Card style={{height: '100%', boxShadow: 'none', ...styles?.card}}>
                <CardHeader action={ClearFilterBtn(id, false)} className={classes.cardHeader} style={cardHeaderStyle}
                            titleTypographyProps={{color: 'textPrimary', variant: 'subtitle2'}}
                            title={t(title)}
                            subheaderTypographyProps={{color: 'textSecondary', variant: 'body2'}}
                            subheader={t(subtitle)}></CardHeader>
                <CardContent style={cardContentStyle}>
					<div> {/* This DIV is needed to center gauge within the Card */}
                    	<div id={id} ref={chartRef} style={{position: 'relative', top: '0px', width: '100%', height: '100%'}}></div>
					</div>
                </CardContent>
            </Card>
        </Root>
    );
}
